import { useEffect, useMemo, useState } from "react";
import type {
  ApiComment,
  ApiCommentPosition,
  CreateCommentInput,
} from "../lib/api";
import { readReviewer } from "../lib/reviewerIdentity";
import {
  CommentCard,
  Composer,
  type OwnerActions,
} from "./CommentParts";

type Props = {
  // Slide-in overlay state. Component is mounted unconditionally so the
  // CSS transition can play in both directions; setting `open=false`
  // animates it off-screen to the right.
  open: boolean;
  onClose: () => void;

  comments: ApiComment[];
  otherPagesCount: number;
  pageUrl: string | null;
  loading: boolean;

  // When the reviewer is in the middle of adding a new comment, the
  // popover handles the composer. The sidebar still indicates the
  // pending state via a small note so it doesn't feel like the click was
  // lost if the popover is dismissed without submitting.
  pendingPosition: ApiCommentPosition | null;
  onCancelPending: () => void;

  // Pre-fills name/email when the current user is the owner.
  defaultIdentity: { name: string; email: string } | null;

  onCreate: (input: CreateCommentInput) => Promise<ApiComment>;
  onEdit: (commentId: string, body: string) => Promise<void>;
  ownerActions: OwnerActions | null;
  onFocusPin: (commentId: string) => void;

  // Controls whether resolved comment pins render on the proxied page.
  // The sidebar list still shows resolved comments under its Resolved
  // tab regardless — this only hides their on-screen pins so the page
  // isn't cluttered with already-handled feedback.
  showResolvedPins: boolean;
  onToggleResolvedPins: () => void;
};

// The right-hand "all comments on this page" list. Slides in from the
// right when the comment-bubble icon in the toolbar is toggled. Always
// rendered (the parent doesn't unmount on close) so the transition is
// reversible. The actual composer / thread-popover work for individual
// pins lives in PinPopover — this surface is the browse view.
export function CommentSidebar({
  open,
  onClose,
  comments,
  otherPagesCount,
  pageUrl,
  loading,
  pendingPosition,
  onCancelPending,
  defaultIdentity,
  onCreate,
  onEdit,
  ownerActions,
  onFocusPin,
  showResolvedPins,
  onToggleResolvedPins,
}: Props) {
  const [filter, setFilter] = useState<"open" | "resolved">("open");
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Close on Escape when open.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const currentUserEmail = (
    defaultIdentity?.email ?? readReviewer()?.email ?? ""
  ).toLowerCase();

  const threads = useMemo(() => {
    const byId = new Map(comments.map((c) => [c.id, c]));
    const tops: ApiComment[] = [];
    const replies = new Map<string, ApiComment[]>();
    for (const c of comments) {
      if (c.parentId && byId.has(c.parentId)) {
        const list = replies.get(c.parentId) ?? [];
        list.push(c);
        replies.set(c.parentId, list);
      } else {
        tops.push(c);
      }
    }
    return tops
      .filter((c) => c.status === filter)
      .map((c) => ({ root: c, replies: replies.get(c.id) ?? [] }));
  }, [comments, filter]);

  const openCount = comments.filter((c) => !c.parentId && c.status === "open").length;
  const resolvedCount = comments.filter((c) => !c.parentId && c.status === "resolved").length;

  return (
    <aside
      aria-hidden={!open}
      className={
        // Absolute overlay: floats over the iframe, slides in from the
        // right. transform/transition gives a soft 200ms slide that's
        // cheaper than animating width.
        "pointer-events-none absolute inset-y-0 right-0 z-20 flex w-80 flex-col border-l border-neutral-200 bg-white shadow-xl transition-transform duration-200 ease-out " +
        (open ? "translate-x-0 pointer-events-auto" : "translate-x-full")
      }
    >
      <div className="flex items-start justify-between border-b border-neutral-200 px-5 pt-5 pb-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">
            Comments
          </h2>
          <div className="mt-3 flex items-center gap-2">
            <div className="inline-flex rounded-md bg-neutral-100 p-0.5 text-xs">
              <FilterTab
                label={`Open · ${openCount}`}
                active={filter === "open"}
                onClick={() => setFilter("open")}
              />
              <FilterTab
                label={`Resolved · ${resolvedCount}`}
                active={filter === "resolved"}
                onClick={() => setFilter("resolved")}
              />
            </div>
            {resolvedCount > 0 ? (
              <button
                type="button"
                onClick={onToggleResolvedPins}
                aria-pressed={showResolvedPins}
                aria-label={
                  showResolvedPins
                    ? "Hide resolved pins on the page"
                    : "Show resolved pins on the page"
                }
                title={
                  showResolvedPins
                    ? "Resolved pins shown — click to hide them on the page"
                    : "Resolved pins hidden — click to show them on the page"
                }
                className={
                  "flex h-6 w-6 items-center justify-center rounded-md transition " +
                  (showResolvedPins
                    ? "text-neutral-700 hover:bg-neutral-100"
                    : "text-neutral-300 hover:bg-neutral-100 hover:text-neutral-500")
                }
              >
                {showResolvedPins ? <EyeIcon /> : <EyeOffIcon />}
              </button>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close comments"
          title="Close"
          className="-mr-2 -mt-1 flex h-7 w-7 items-center justify-center rounded-md text-neutral-400 hover:bg-neutral-100 hover:text-neutral-900"
        >
          <CloseIcon />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {pendingPosition ? (
          <div className="flex items-start gap-3 border-b border-neutral-200 bg-amber-50 px-5 py-3 text-xs text-amber-900">
            <span aria-hidden>📍</span>
            <div className="flex-1">
              <p className="font-medium">Composing a new comment</p>
              <p className="mt-0.5 text-amber-800/80">
                The composer is open at the pin you clicked.
              </p>
            </div>
            <button
              type="button"
              onClick={onCancelPending}
              className="text-[11px] underline hover:no-underline"
            >
              Cancel
            </button>
          </div>
        ) : null}

        {loading && threads.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-neutral-400">
            Loading comments…
          </div>
        ) : threads.length === 0 ? (
          <EmptyState
            filter={filter}
            hasComments={openCount + resolvedCount > 0}
            otherPagesCount={otherPagesCount}
          />
        ) : null}

        <ul className="divide-y divide-neutral-100">
          {threads.map(({ root, replies }, idx) => {
            const rootEditable =
              !!currentUserEmail && root.authorEmail.toLowerCase() === currentUserEmail;
            const tourAnchorReply = idx === 0;
            const tourAnchorEdit = idx === 0 && rootEditable;
            return (
              <li key={root.id} className="px-5 py-4">
                <CommentCard
                  comment={root}
                  editing={editingId === root.id}
                  editable={rootEditable}
                  tourReplyAnchor={tourAnchorReply}
                  tourEditAnchor={tourAnchorEdit}
                  onStartEdit={() => setEditingId(root.id)}
                  onCancelEdit={() => setEditingId(null)}
                  onSaveEdit={async (next) => {
                    await onEdit(root.id, next);
                    setEditingId(null);
                  }}
                  onResolve={ownerActions?.onResolve}
                  onReopen={ownerActions?.onReopen}
                  onDelete={ownerActions?.onDelete}
                  onReply={() => setReplyingTo(root.id)}
                  onFocusPin={() => onFocusPin(root.id)}
                />
                {replies.length > 0 ? (
                  <ul className="mt-3 ml-4 space-y-3 border-l border-neutral-200 pl-3">
                    {replies.map((r) => {
                      const replyEditable =
                        !!currentUserEmail && r.authorEmail.toLowerCase() === currentUserEmail;
                      return (
                        <li key={r.id}>
                          <CommentCard
                            comment={r}
                            compact
                            editing={editingId === r.id}
                            editable={replyEditable}
                            onStartEdit={() => setEditingId(r.id)}
                            onCancelEdit={() => setEditingId(null)}
                            onSaveEdit={async (next) => {
                              await onEdit(r.id, next);
                              setEditingId(null);
                            }}
                            onDelete={ownerActions?.onDelete}
                          />
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
                {replyingTo === root.id && pageUrl ? (
                  <div className="mt-3 ml-4 border-l border-neutral-200 pl-3">
                    <Composer
                      pageUrl={pageUrl}
                      position={root.position}
                      parentId={root.id}
                      defaultIdentity={defaultIdentity}
                      onCreate={onCreate}
                      onCancel={() => setReplyingTo(null)}
                      kind="reply"
                    />
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>

      {otherPagesCount > 0 ? (
        <div className="border-t border-neutral-200 px-5 py-3 text-xs text-neutral-500">
          {otherPagesCount} {otherPagesCount === 1 ? "comment" : "comments"} on other pages — navigate inside the preview to see them.
        </div>
      ) : null}
    </aside>
  );
}

function FilterTab({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "rounded px-2.5 py-1 transition " +
        (active ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-500 hover:text-neutral-900")
      }
    >
      {label}
    </button>
  );
}

function EmptyState({
  filter,
  hasComments,
  otherPagesCount,
}: {
  filter: "open" | "resolved";
  hasComments: boolean;
  otherPagesCount: number;
}) {
  if (filter === "open") {
    return (
      <div className="px-5 py-10 text-center text-sm text-neutral-500">
        {hasComments ? (
          <p>No open comments on this page.</p>
        ) : otherPagesCount > 0 ? (
          <p>No comments on this page yet.</p>
        ) : (
          <p>Click anywhere on the page to leave the first comment.</p>
        )}
      </div>
    );
  }
  return (
    <div className="px-5 py-10 text-center text-sm text-neutral-500">
      Nothing resolved yet.
    </div>
  );
}

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9.9 5.2A9.5 9.5 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3 3.8M6.6 6.6A17 17 0 0 0 2 12s3.5 7 10 7a9.5 9.5 0 0 0 4-.9" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
      <path d="M3 3l18 18" />
    </svg>
  );
}
